<template>
    <div
        :class="[
            'flex flex-col container bg-slate-50 border border-blue-300 rounded-lg p-4 relative',
            { deactivate: jobInfo.is_expired, 'show-new-badge': shouldShowBadge },
        ]"
    >
        <div
            :id="`job-post${jobInfo.id}`"
            class="job-post"
        >
            <div v-if="undoVibe && isFromSavedList">
                <Btn
                    :label="t('undo')"
                    variant="text"
                    left-icon="undo"
                    size="xxs"
                    @click="undoStep"
                />
            </div>
            <div class="flex flex-row flex-wrap md:flex-nowrap">
                <div class="company-logo flex mr-4 flex-grow sm:justify-center sm:flex-grow-0">
                    <img
                        :src="jobInfo.logo"
                        class="border border-slate-200 w-12 h-12 object-contain"
                    />
                </div>
                <div class="flex flex-col overflow-hidden w-full sm:w-auto order-last sm:order-none">
                    <div
                        class="text-xl leading-7 font-bold truncate"
                        :data-test-id="`${dataTestRoot}-job-title`"
                    >
                        {{ jobInfo.job_title }}
                    </div>
                    <a
                        v-if="!companyProfileWidget"
                        :data-test-id="`${dataTestRoot}-job-company-link`"
                        :class="[
                            'text-md leading-4 mt-2',
                            jobInfo.company_website ? 'text-blue-600 hover:text-blue-200' : 'text-black cursor-default',
                        ]"
                        :href="companyLink"
                        target="_blank"
                    >
                        {{ jobInfo.company_name }}
                    </a>
                    <p
                        v-else
                        class="text-black cursor-default text-sm leading-4 mt-2"
                    >
                        {{ jobInfo.company_name }}
                    </p>
                </div>
                <div
                    v-if="jobInfo.score"
                    :data-test-id="`${dataTestRoot}-job-matching-score-span`"
                    class="score flex-none justify-self-end ml-auto"
                >
                    <Percentage :value="jobInfo.score" />
                </div>
            </div>
            <div class="flex flex-row mt-4 text-sm truncate-text">
                {{ jobInfo.desc }}
            </div>
            <div
                v-if="salary"
                class="mt-4 mb-2 text-sm font-medium w-max py-1 px-2 bg-green-100 text-green-500 rounded"
            >
                <span class="flex items-center">
                    {{ salary }}
                    <i
                        v-if="hasSalaryDetails"
                        v-tooltip="{
                            content: salaryDetails,
                            showTriggers: ['hover', 'click'],
                        }"
                        class="icon icon-info w-5 h-5 ml-1"
                    ></i>
                </span>
            </div>
            <div class="flex flex-row flex-wrap items-center">
                <div
                    class="flex flex-row flex-wrap flex-grow text-slate-600 w-1/2 my-4 md:my-0 gap-x-2 overflow-hidden"
                >
                    <div
                        v-for="(option, index) in options"
                        :key="index"
                        class="job-option-item"
                    >
                        <div
                            v-if="!isEmpty(option)"
                            class="option-item flex w-full items-center"
                        >
                            <i :class="`icon ${index} w-5 h-5 mr-2`" />
                            <span class="truncate-text-icon">{{ getOptionsValues(option) }}</span>
                        </div>
                    </div>
                </div>
                <div class="flex justify-end ml-0 md:ml-4 w-full md:w-auto">
                    <Btn
                        v-if="!companyProfileWidget"
                        :data-test-id="
                            vibed ? `${dataTestRoot}-job-heart-btn` : `${dataTestRoot}-job-unselected-heart-btn`
                        "
                        :icon="vibed ? 'heart-full' : 'heart'"
                        variant="outlined"
                        class="text-green-500 mr-2"
                        :class="['text-green-500 mr-2', { 'enable-heart-icon': jobInfo.is_expired }]"
                        @click="vibeJob"
                    ></Btn>
                    <!-- native modifier has been removed, please confirm whether the function has been affected  -->
                    <Btn
                        :data-test-id="`${dataTestRoot}-job-details-btn`"
                        :right-icon="jobInfo.is_expired ? '' : 'arrow-right'"
                        :label="actionButtonText"
                        :href="jobInfo.url"
                        variant="filled"
                        color="primary"
                        class="btn-link w-full md:w-auto"
                        :data-click="`job widget,${jobInfo.url}`"
                        @click.prevent="openLink('ApplicationInterest', jobInfo.id, jobInfo.url)"
                    ></Btn>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { trackEvent } from '@/modules/tracking';
import JobItemMixin from '@components/CareerCornerMatching/JobItemMixin';
import { useTranslation } from 'i18next-vue';
import Percentage from './Percentage.vue';

const ACTION_VIBED = 1;

export default {
    name: 'JobPost',

    components: {
        Percentage,
    },

    extends: JobItemMixin,

    props: {
        companyProfileWidget: {
            type: Boolean,
            required: false,
            default: false,
        },
        dataTestRoot: {
            type: String,
            default: null,
        },
        isFromSavedList: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['update-vibe', 'application-interest-clicked'],

    setup() {
        const { t } = useTranslation('career-corner');
        return { t };
    },

    data() {
        return {
            vibed: null,
            undoVibe: false,
            updateVibe: null,
        };
    },

    computed: {
        actionButtonText() {
            return this.jobInfo.is_expired ? this.t('job_list.expired') : this.t('job_list.apply');
        },
        shouldShowBadge() {
            return this.jobInfo.marked_as_new;
        },
    },
    watch: {
        // eslint-disable-next-line func-names
        'jobInfo.action': function (newVal) {
            this.vibed = newVal === ACTION_VIBED;
        },
    },

    created() {
        this.vibed = this.jobInfo.action === ACTION_VIBED;
    },

    methods: {
        vibeJob() {
            this.vibed = !this.vibed;
            if (this.isFromSavedList) {
                document.getElementById(`job-post${this.jobInfo.id}`).classList.toggle('collapsed');
                /* undo button should be shown using the same ms of the css animation */
                setTimeout(() => {
                    this.undoVibe = !this.vibed;
                }, 300);

                this.updateVibe = setTimeout(() => {
                    this.$emit('update-vibe', this.jobInfo.id, this.vibed);
                }, 3000);
            } else {
                this.$emit('update-vibe', this.jobInfo.id, this.vibed);
            }
        },
        undoStep() {
            document.getElementById(`job-post${this.jobInfo.id}`).classList.toggle('collapsed');

            clearTimeout(this.updateVibe);
            this.undoVibe = false;
            this.vibed = true;
        },
        openLink(trackingName, jobId, url) {
            trackEvent('CareerMatching', trackingName, jobId);
            window.open(url, '_blank');
            this.$emit('application-interest-clicked');
        },
    },
};
</script>

<style lang="scss" scoped>
.deactivate {
    position: relative;
}
.deactivate:after {
    content: ' ';
    z-index: 1;
    display: block;
    position: absolute;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.5);
}
.job-option-item {
    flex-basis: max-content;
    max-width: 100%;
}

.enable-heart-icon {
    z-index: 10;
    opacity: 0.5;
}

.enable-heart-icon {
    z-index: 10;
    opacity: 0.5;
}

.truncate-text {
    display: -webkit-box;
    max-width: 100%;
    word-break: break-word;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 3;
}

.company-logo {
    min-width: 64px;
    max-width: 64px;
    height: 50px;
    img {
        max-height: 50px;
    }
}
.score {
    width: 60px;
    height: 60px;
}

.icon {
    &.job_type {
        mask-image: url('/images/icons/icon-job.svg');
    }
    &.work_environment {
        mask-image: url('/images/icons/icon-laptop.svg');
    }
    &.employment_type {
        mask-image: url('/images/icons/icon-clock.svg');
    }
    &.location {
        mask-image: url('/images/icons/icon-location.svg');
    }
    &.industry {
        mask-image: url('/images/icons/icon-company.svg');
    }
}

.job-post {
    overflow: hidden;
    transition: max-height 0.3s ease-out;
    max-height: 600px;
}
.job-post.collapsed {
    max-height: 28px;
}

.show-new-badge::before {
    content: '';
    position: absolute;
    top: -1px;
    right: -1px;
    width: 53px;
    height: 53px;
    background: url('/images/badges/badge_new.svg') no-repeat right top;
    background-size: contain;
    z-index: 1;
}
</style>
